import React, { useState, useEffect, useMemo } from 'react';
import { getPaymentStatusColor } from '../../utils/statusBadge';
import {
  Box,
  VStack,
  HStack,
  Text,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  Card,
  CardHeader,
  CardBody,
  Input,
  InputGroup,
  InputLeftElement,
  Select,
  Icon,
  Flex,
  Spinner,
  useColorModeValue,
  Tooltip,
  Code,
  Button
} from '@chakra-ui/react';
import {
  FaSearch,
  FaDollarSign,
  FaCheckCircle,
  FaTimesCircle,
  FaClock,
  FaCreditCard,
  FaCalendarAlt,
  FaExternalLinkAlt
} from 'react-icons/fa';
import { supabase } from '../../supabaseClient';
import { useToast } from '@chakra-ui/react';

const PaymentHistory = ({ isActive = false }) => {
  const [payments, setPayments] = useState([]);
  const [labs, setLabs] = useState({});
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(25);
  const toast = useToast();
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');

  useEffect(() => {
    fetchPayments();
  }, []);

  // Refresh when tab becomes active
  useEffect(() => {
    if (isActive) {
      fetchPayments();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActive]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, statusFilter]);

  const fetchPayments = async () => {
    try {
      setLoading(true);
      
      // Fetch billing transactions
      const { data: transactions, error: txError } = await supabase
        .from('billing_transactions')
        .select('*')
        .order('transaction_date', { ascending: false });

      if (txError) {
        if (txError.code === '42P01') {
          toast({
            title: 'Table Not Found',
            description: 'The billing_transactions table does not exist. Payment history will be empty until transactions are created.',
            status: 'warning',
            duration: 5000,
            isClosable: true,
          });
          setPayments([]);
          return;
        }
        throw txError;
      }

      // Fetch labs separately and build map
      const labIds = [...new Set((transactions || []).map(tx => tx.lab_id).filter(Boolean))];
      let labsMap = {};
      
      if (labIds.length > 0) {
        const { data: labsData, error: labsError } = await supabase
          .from('labs')
          .select('id, name, contact_email')
          .in('id', labIds);

        if (!labsError && labsData) {
          labsData.forEach(lab => {
            labsMap[lab.id] = lab;
          });
        }
      }

      setLabs(labsMap);
      setPayments(transactions || []);
    } catch (error) {
      console.error('Error fetching payment history:', error);
      toast({
        title: 'Error',
        description: `Failed to fetch payment history: ${error.message}`,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      setPayments([]);
    } finally {
      setLoading(false);
    }
  };

  const filteredPayments = useMemo(() => {
    let filtered = payments;

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(payment =>
        labs[payment.lab_id]?.name?.toLowerCase().includes(term) ||
        payment.description?.toLowerCase().includes(term) ||
        payment.stripe_transaction_id?.toLowerCase().includes(term) ||
        payment.stripe_payment_intent_id?.toLowerCase().includes(term)
      );
    }

    if (statusFilter !== 'all') {
      filtered = filtered.filter(payment => payment.status === statusFilter);
    }

    return filtered;
  }, [payments, searchTerm, statusFilter, labs]);

  const totalPages = Math.ceil(filteredPayments.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedPayments = filteredPayments.slice(startIndex, startIndex + itemsPerPage);

  // Calculate total amount paid (only completed transactions)
  const totalAmountPaid = useMemo(() => {
    return filteredPayments
      .filter(p => p.status === 'completed')
      .reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0);
  }, [filteredPayments]);

  const getStatusColor = (status) => getPaymentStatusColor(status);

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return FaCheckCircle;
      case 'failed':
        return FaTimesCircle;
      case 'pending':
        return FaClock;
      default:
        return FaCreditCard;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  const openStripeLink = (paymentIntentId) => {
    if (paymentIntentId) {
      window.open(`https://dashboard.stripe.com/payments/${paymentIntentId}`, '_blank');
    }
  };

  if (loading) {
    return (
      <Box p={6}>
        <Flex justify="center" align="center" minH="400px">
          <VStack spacing={4}>
            <Spinner size="xl" color="blue.500" />
            <Text color="gray.600">Loading payment history...</Text>
          </VStack>
        </Flex>
      </Box>
    );
  }

  return (
    <Box p={6}>
      <VStack spacing={6} align="stretch">
        {/* Header */}
        <Flex align="center" justify="space-between">
          <HStack spacing={3}>
            <Icon as={FaCreditCard} boxSize={6} color="blue.500" />
            <Text fontSize="2xl" fontWeight="bold" color="gray.800">
              Payment History
            </Text>
          </HStack>
          <Badge colorScheme="blue" fontSize="md" px={3} py={1}>
            {filteredPayments.length} {filteredPayments.length === 1 ? 'Payment' : 'Payments'}
          </Badge>
        </Flex>

        {/* Filters */}
        <Card bg={cardBg} borderColor={borderColor} borderWidth="1px" boxShadow="sm">
          <CardBody p={4}>
            <HStack spacing={4} flexWrap="wrap">
              <InputGroup flex={1} minW="200px">
                <InputLeftElement pointerEvents="none">
                  <Icon as={FaSearch} color="gray.400" />
                </InputLeftElement>
                <Input
                  placeholder="Search lab, description, or Stripe ID..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  bg="white"
                  borderColor="gray.300"
                  _hover={{ borderColor: "gray.400" }}
                  _focus={{ borderColor: "blue.500", boxShadow: "0 0 0 1px #3182ce" }}
                />
              </InputGroup>
              <Select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                maxW="200px"
                bg="white"
                borderColor="gray.300"
                _hover={{ borderColor: "gray.400" }}
                _focus={{ borderColor: "blue.500", boxShadow: "0 0 0 1px #3182ce" }}
              >
                <option value="all">All Statuses</option>
                <option value="completed">Completed</option>
                <option value="pending">Pending</option>
                <option value="failed">Failed</option>
                <option value="refunded">Refunded</option>
                <option value="cancelled">Cancelled</option>
              </Select>
            </HStack>
          </CardBody>
        </Card>

        {/* Payments Table */}
        <Card bg={cardBg} borderColor={borderColor} borderWidth="1px" boxShadow="sm" overflow="hidden">
          <CardHeader bg="gray.50" borderBottom="1px solid" borderColor={borderColor}>
            <Text fontWeight="semibold" color="gray.700">Recent Payments</Text>
          </CardHeader>
          <CardBody p={0}>
            {paginatedPayments.length === 0 ? (
              <Box p={8} textAlign="center">
                <VStack spacing={3}>
                  <Icon as={FaCreditCard} boxSize={12} color="gray.400" />
                  <Text color="gray.600" fontWeight="semibold">
                    No payments found
                  </Text>
                  <Text color="gray.500" fontSize="sm">
                    {searchTerm || statusFilter !== 'all'
                      ? 'Try adjusting your filters'
                      : 'Payment history will appear here once transactions are processed'}
                  </Text>
                </VStack>
              </Box>
            ) : (
              <>
                <Table size="md">
                  <Thead bg="gray.50">
                    <Tr>
                      <Th fontWeight="semibold" color="gray.700" py={4}>Date</Th>
                      <Th fontWeight="semibold" color="gray.700" py={4}>Lab</Th>
                      <Th fontWeight="semibold" color="gray.700" py={4}>Description</Th>
                      <Th fontWeight="semibold" color="gray.700" py={4} isNumeric>Amount</Th>
                      <Th fontWeight="semibold" color="gray.700" py={4}>Status</Th>
                      <Th fontWeight="semibold" color="gray.700" py={4}>Stripe ID</Th>
                      <Th fontWeight="semibold" color="gray.700" py={4}>Actions</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {paginatedPayments.map((payment, index) => (
                      <Tr
                        key={payment.id}
                        bg={index % 2 === 0 ? 'white' : 'gray.25'}
                        _hover={{ bg: 'gray.50' }}
                        transition="background 0.2s"
                      >
                        <Td py={4}>
                          <HStack spacing={2}>
                            <Icon as={FaCalendarAlt} color="gray.400" boxSize={4} />
                            <Text fontSize="sm" color="gray.700">
                              {formatDate(payment.transaction_date)}
                            </Text>
                          </HStack>
                        </Td>
                        <Td py={4}>
                          <Text fontWeight="semibold" color="gray.800" fontSize="sm">
                            {labs[payment.lab_id]?.name || 'Unknown Lab'}
                          </Text>
                          {labs[payment.lab_id]?.contact_email && (
                            <Text fontSize="xs" color="gray.500">
                              {labs[payment.lab_id].contact_email}
                            </Text>
                          )}
                        </Td>
                        <Td py={4}>
                          <Text fontSize="sm" color="gray.700" noOfLines={2}>
                            {payment.description || 'No description'}
                          </Text>
                          {payment.type && (
                            <Badge colorScheme="blue" variant="subtle" fontSize="xs" mt={1}>
                              {payment.type}
                            </Badge>
                          )}
                        </Td>
                        <Td py={4} isNumeric>
                          <Text
                            fontWeight="bold"
                            color={payment.status === 'completed' ? 'green.600' : 'gray.800'}
                            fontSize="sm"
                          >
                            {formatCurrency(payment.amount)}
                          </Text>
                        </Td>
                        <Td py={4}>
                          <Badge
                            colorScheme={getStatusColor(payment.status)}
                            variant="solid"
                            fontSize="xs"
                            px={2}
                            py={1}
                          >
                            <HStack spacing={1}>
                              <Icon as={getStatusIcon(payment.status)} boxSize={3} />
                              <Text>{payment.status || 'unknown'}</Text>
                            </HStack>
                          </Badge>
                          {payment.failure_reason && (
                            <Text fontSize="xs" color="red.600" mt={1}>
                              {payment.failure_reason}
                            </Text>
                          )}
                        </Td>
                        <Td py={4}>
                          {payment.stripe_payment_intent_id ? (
                            <Tooltip label="Click to open in Stripe Dashboard">
                              <Code
                                fontSize="xs"
                                p={1}
                                borderRadius="md"
                                cursor="pointer"
                                onClick={() => openStripeLink(payment.stripe_payment_intent_id)}
                                _hover={{ bg: 'gray.100' }}
                              >
                                {payment.stripe_payment_intent_id.substring(0, 20)}...
                              </Code>
                            </Tooltip>
                          ) : (
                            <Text fontSize="xs" color="gray.400">N/A</Text>
                          )}
                        </Td>
                        <Td py={4}>
                          {payment.stripe_payment_intent_id && (
                            <Button
                              size="xs"
                              leftIcon={<FaExternalLinkAlt />}
                              variant="outline"
                              colorScheme="blue"
                              onClick={() => openStripeLink(payment.stripe_payment_intent_id)}
                            >
                              View
                            </Button>
                          )}
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>

                {/* Pagination / summary */}
                <Box p={4} borderTop="1px solid" borderColor={borderColor}>
                  <HStack justify="space-between" flexWrap="wrap">
                    <Text fontSize="sm" color="gray.600">
                      {filteredPayments.length === 0
                        ? 'No payments to display'
                        : `Showing ${startIndex + 1} to ${Math.min(startIndex + itemsPerPage, filteredPayments.length)} of ${filteredPayments.length} payments`}
                    </Text>
                    <HStack spacing={2}>
                      <Button
                        size="sm"
                        onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                        isDisabled={currentPage === 1 || totalPages <= 1}
                      >
                        Previous
                      </Button>
                      <Text fontSize="sm" fontWeight="bold" color="gray.800">
                        Page {currentPage} of {Math.max(1, totalPages)}
                      </Text>
                      <Button
                        size="sm"
                        onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                        isDisabled={currentPage === totalPages || totalPages <= 1}
                      >
                        Next
                      </Button>
                    </HStack>
                  </HStack>
                </Box>
              </>
            )}
          </CardBody>
        </Card>
      </VStack>
    </Box>
  );
};

export default PaymentHistory;
